import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Button
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { platformApiFetch } from './platformApi';

const STATUSES = ['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'];
const PRIORITIES = ['low', 'normal', 'high', 'urgent'];

function statusColor(s) {
  if (s === 'open') return 'info';
  if (s === 'resolved' || s === 'closed') return 'success';
  if (s === 'waiting_customer') return 'warning';
  return 'primary';
}

export default function PlatformSupportTicketsListPage() {
  const [tenants, setTenants] = useState([]);
  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [tenantInput, setTenantInput] = useState('');
  const [appliedTenantId, setAppliedTenantId] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');
  const [priorityInput, setPriorityInput] = useState('');
  const [appliedPriority, setAppliedPriority] = useState('');
  const [dateFromInput, setDateFromInput] = useState('');
  const [appliedDateFrom, setAppliedDateFrom] = useState('');
  const [dateToInput, setDateToInput] = useState('');
  const [appliedDateTo, setAppliedDateTo] = useState('');
  const [sort, setSort] = useState('opened_at');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await platformApiFetch('/api/platform/tenants?pageSize=500&sort=tenant_name&order=asc');
        const json = await res.json().catch(() => ({}));
        if (res.ok && !cancelled) setTenants(json.tenants || []);
      } catch (_e) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: String(page + 1),
      pageSize: String(pageSize),
      sort,
      order
    });
    if (appliedQ) params.set('q', appliedQ);
    if (appliedTenantId) params.set('tenantId', appliedTenantId);
    if (appliedStatus) params.set('status', appliedStatus);
    if (appliedPriority) params.set('priority', appliedPriority);
    if (appliedDateFrom) params.set('dateFrom', appliedDateFrom);
    if (appliedDateTo) params.set('dateTo', appliedDateTo);
    try {
      const res = await platformApiFetch(`/api/platform/support-tickets?${params}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load tickets');
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    appliedQ,
    appliedTenantId,
    appliedStatus,
    appliedPriority,
    appliedDateFrom,
    appliedDateTo,
    sort,
    order
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = () => {
    setPage(0);
    setAppliedQ(qInput.trim());
    setAppliedTenantId(tenantInput);
    setAppliedStatus(statusInput);
    setAppliedPriority(priorityInput);
    setAppliedDateFrom(dateFromInput.trim());
    setAppliedDateTo(dateToInput.trim());
  };

  const rows = data?.tickets || [];
  const total = data?.pagination?.total ?? 0;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Support tickets
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Cross-tenant queue. Analysts can review; super admins and support roles can update and reply internally.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <TextField
            size="small"
            label="Search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            sx={{ minWidth: 200 }}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Tenant</InputLabel>
            <Select label="Tenant" value={tenantInput} onChange={(e) => setTenantInput(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {tenants.map((t) => (
                <MenuItem key={t.id} value={String(t.id)}>
                  {t.tenant_name} ({t.tenant_code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusInput} onChange={(e) => setStatusInput(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Priority</InputLabel>
            <Select label="Priority" value={priorityInput} onChange={(e) => setPriorityInput(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {PRIORITIES.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Opened from"
            type="date"
            value={dateFromInput}
            onChange={(e) => setDateFromInput(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="Opened to"
            type="date"
            value={dateToInput}
            onChange={(e) => setDateToInput(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Sort</InputLabel>
            <Select label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
              <MenuItem value="opened_at">Opened</MenuItem>
              <MenuItem value="updated_at">Updated</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Order</InputLabel>
            <Select label="Order" value={order} onChange={(e) => setOrder(e.target.value)}>
              <MenuItem value="desc">Desc</MenuItem>
              <MenuItem value="asc">Asc</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={applyFilters}>
            Apply
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ticket</TableCell>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Opened</TableCell>
                  <TableCell align="right">View</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {row.ticket_number || `#${row.id}`}
                    </TableCell>
                    <TableCell>
                      {row.tenant_name || row.tenant_code || row.tenant_id}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }} title={row.subject}>
                      {row.subject}
                    </TableCell>
                    <TableCell>{row.priority}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status.replace(/_/g, ' ')} color={statusColor(row.status)} variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.opened_at ? String(row.opened_at) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton component={Link} to={`/platform/support-tickets/${row.id}`} size="small">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>No tickets match.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
